import {
  GetConsultantEventLogEvents,
  useGetSwitchesDataQuery,
  useResetUnfitFlagMutation,
  useSetUnfitFlagMutation
} from "@bonliva-crm/graphql";
import { AlertDialog, RadioButtonGroup, TextField } from "@bonliva-crm/ui";
import { useLocale, useSnackbars } from "@hooks";
import {
  Box,
  Switch,
  Typography,
  makeStyles,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Dialog,
  Grid
} from "@material-ui/core";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { ConsultantUnfitType } from "@bonliva-crm/shared";
import yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";

type Props = {
  consultantId: string;
};

type FormValues = {
  unfitType: string;
  comment: string;
};

const schema = yup.object<FormValues>({
  unfitType: yup.string().required(),
  comment: yup.string().when("unfitType", {
    is: ConsultantUnfitType.Unfit,
    then: yup.string().required()
  })
});

export function UnfitSwitch(props: Props) {
  const { showError } = useSnackbars();
  const { locale } = useLocale();
  const classes = useStyles();

  const [modalFormOpen, setModalFormOpen] = useState(false);
  const [dialogContent, setDialogContent] = useState("");

  const form = useForm<FormValues>({
    defaultValues: {
      unfitType: ConsultantUnfitType.Unfit,
      comment: ""
    },
    resolver: yupResolver(schema)
  });

  useEffect(() => {
    form.reset();
    form.trigger();
  }, [modalFormOpen]);

  const { unfitType, comment } = form.watch();

  useEffect(() => {
    form.trigger();
  }, [unfitType, comment]);

  const { data } = useGetSwitchesDataQuery({
    variables: { id: props.consultantId },
    fetchPolicy: "cache-first"
  });

  const [setUnfitFlagMutation] = useSetUnfitFlagMutation({
    onError: (error) => {
      showError(locale.cannotPerformAction);
      console.error(error);
    },
    refetchQueries: [GetConsultantEventLogEvents.OperationName]
  });

  const [resetUnfitFlagMutation] = useResetUnfitFlagMutation({
    onError: (error) => {
      showError(locale.cannotPerformAction);
      console.error(error);
    },
    refetchQueries: [GetConsultantEventLogEvents.OperationName]
  });

  const setUnfitFlag = (values: FormValues) => {
    return setUnfitFlagMutation({
      variables: {
        consultantId: props.consultantId,
        unfitType: values.unfitType as ConsultantUnfitType,
        comment: values.comment
      },
      onCompleted: () => {
        setModalFormOpen(false);
      }
    });
  };

  const resetUnfitFlag = () => {
    resetUnfitFlagMutation({
      variables: {
        consultantId: props.consultantId
      },
      optimisticResponse: () => {
        return {
          resetConsultantUnfit: {
            ...data.consultantById,
            Unfit: false,
            UnfitComment: null,
            UnfitedBy: null
          }
        };
      }
    });
  };

  const handleDialog = (unfit: boolean) => {
    const alertContent = unfit
      ? `Vi kommer slå av:
        - Kvalitetsuppdateringar
        - Marknadsföring (vi skickar ett automatiskt mail till marknadsavdelningen att denna konsulten nu har status “${
          locale.consultantUnfitType[form.getValues().unfitType]
        }”)
        - Behovsutskick
        - Konsulten kommer bli borttagen från de ringlistor den är med i

        OBS: vi tar inte bort tillgången till mobilappen ifall konsulten fortfarande har aktiva tidrapporter. Vänligen slå av denna manuellt om du vill.`
      : `När konsulten taggades som olämplig så slog vi av följande funktioner:
        - Kvalitetsuppdateringar
        - Marknadsföring
        - Behovsutskick
        - Ringlistor

        Vänligen gå över dessa manuellt om du vill slå tillbaks tillgången. OBS vi skickar inte ett automatiskt mail till marknadsavdelningen att denna konsultens status är “Lämplig” igen så gör detta om du vill lägga till personen i kommande marknadsutskick.
      `;

    setDialogContent(alertContent);
  };

  const hint =
    "Om konsulten har begärt att bli raderad från våra system måste du hänvisa konsulten att maila dataskydd@bonliva.se med sin förfrågan.";

  const onCloseDialog = () => {
    setDialogContent("");
  };

  const onModalFormClose = () => {
    setModalFormOpen(false);
  };

  return (
    <Box className={classes.root}>
      <AlertDialog
        open={!!dialogContent}
        title={locale.doYouWantToContinue}
        onClose={onCloseDialog}
        onOk={() => {
          if (data?.consultantById.Unfit) {
            resetUnfitFlag();
          } else {
            form.handleSubmit(setUnfitFlag)();
          }
          onCloseDialog();
        }}
      >
        {dialogContent}
      </AlertDialog>

      <Dialog open={modalFormOpen} onClose={onModalFormClose}>
        <DialogTitle>{locale.UnfitConsultant}</DialogTitle>

        <DialogContent style={{ width: 500 }}>
          <Grid container direction={"column"} spacing={2}>
            <Grid item>
              <Controller
                control={form.control}
                name="unfitType"
                render={({ field }) => (
                  <RadioButtonGroup
                    {...field}
                    helperText={
                      field.value === ConsultantUnfitType.NotContactable ? hint : ""
                    }
                    options={Object.values(ConsultantUnfitType).map((type) => ({
                      label: locale.consultantUnfitType[type],
                      value: type
                    }))}
                  />
                )}
              />
            </Grid>

            <Grid item>
              <Controller
                control={form.control}
                name="comment"
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={locale.Comment}
                    error={!!form.formState.errors.comment}
                    fullWidth
                    multiline
                    minRows={2}
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button variant="contained" onClick={onModalFormClose}>
            {locale.Cancel}
          </Button>

          <Button
            type={"submit"}
            disabled={form.formState.isSubmitting || !form.formState.isValid}
            variant="contained"
            color="primary"
            onClick={() => handleDialog(true)}
          >
            {locale.Save}
          </Button>
        </DialogActions>
      </Dialog>

      <Switch
        checked={data?.consultantById.Unfit ?? false}
        onClick={() => {
          data?.consultantById.Unfit ? handleDialog(false) : setModalFormOpen(true);
        }}
      />

      <Box>
        <Typography variant="body1" style={{ paddingTop: "2px" }}>
          {locale.UnfitConsultant}
          {data?.consultantById.Unfit
            ? ` • ${locale.consultantUnfitType[data.consultantById.unfitType]}`
            : ""}
        </Typography>

        {data?.consultantById.Unfit && (
          <>
            <Box className={classes.textContainer}>
              <Typography variant="subtitle1" style={{ fontWeight: 500 }}>
                {locale.enabledBy}
              </Typography>
              <div style={{ display: "flex", flexDirection: "column" }}>
                <Typography variant="subtitle1">
                  {data?.consultantById.UnfitedBy?.Name}{" "}
                </Typography>
                {data?.consultantById.UnfitedSetOn && (
                  <Typography variant="subtitle1">
                    {moment(data?.consultantById.UnfitedSetOn).format("YYYY-MM-DD")}
                  </Typography>
                )}
              </div>
            </Box>

            <Box className={classes.textContainer}>
              <Typography variant="subtitle1" style={{ fontWeight: 500 }}>
                {locale.Comment}
                {": "}
              </Typography>
              <Typography variant="subtitle1">
                {data?.consultantById.UnfitComment}
              </Typography>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
}

const useStyles = makeStyles({
  root: {
    display: "flex",
    gap: "1rem",
    alignItems: "start"
  },
  textContainer: {
    display: "flex",
    flexDirection: "row",
    gap: "4px",
    alignItems: "start",
    marginTop: "4px"
  }
});
